import PaginationProvider, {
  usePagination
} from '@jetshop/core/components/Pagination/PaginationProvider';
import SortOrderContainer from '@jetshop/core/components/SortOrder/SortOrderContainer';
import GenericError from '@jetshop/ui/ErrorBoundary/Generic';
import React, { useEffect } from 'react';
import { useQuery, useApolloClient } from 'react-apollo';
import { useHistory, useLocation } from 'react-router';
import throwErrorInDev from '@jetshop/core/helpers/throwErrorInDev';
import Head from '@jetshop/core/components/Head';
import ActiveFiltersQuery from '@jetshop/core/components/DynamicRoute/ActiveFiltersQuery.gql';
import ProductByArticlenumber from '@jetshop/core/components/DynamicRoute/ProductByArticlenumber';
import { defaultActiveFilters } from '@jetshop/core/resolvers/filters/defaultActiveFilters';
import { getActiveFiltersFromSearch } from '@jetshop/core/resolvers/filters/filterParams';
import RouteResolver from './RouteResolver';

function useFiltersOnRouteChange() {
  const history = useHistory();
  const client = useApolloClient();

  useEffect(() => {
    client.writeData({
      data: {
        activeFilters: getActiveFiltersFromSearch(window.location.search)
      }
    });
  }, [client, history]);
}

const DynamicRoute = props => {
  throwErrorInDev(
    !props.routeQuery,
    `You forgot to pass 'routeQuery' to DynamicRoute`
  );

  const pagination = usePagination();

  if (pagination === undefined) {
    return (
      <PaginationProvider>
        <DynamicRoute {...props} />
      </PaginationProvider>
    );
  }

  const {
    productPage,
    categoryPage,
    contentPage,
    notFoundPage,
    LoadingPage,
    ErrorPage,
    location,
    routeQuery
  } = props;

  if (location.pathname.substr(0, 14) === '/articlenumber') {
    const articleNumber = location.pathname.substr(15);
    return <ProductByArticlenumber articleNumber={articleNumber} />;
  }

  if (location.pathname === '/undefined') {
    return null;
  }

  return (
    <SortOrderContainer>
      {({ sortOrder, direction }) => (
        <RouteQuery
          key={location.pathname}
          pagination={pagination}
          sortOrder={sortOrder}
          direction={direction}
          routeQuery={routeQuery}
          productPage={productPage}
          categoryPage={categoryPage}
          contentPage={contentPage}
          notFoundPage={notFoundPage}
          LoadingPage={LoadingPage}
          ErrorPage={ErrorPage}
        />
      )}
    </SortOrderContainer>
  );
};

const RouteQuery = ({
  pagination,
  sortOrder,
  direction,
  routeQuery,
  productPage,
  categoryPage,
  contentPage,
  notFoundPage,
  LoadingPage,
  ErrorPage
}) => {
  const location = useLocation();
  const { perPage, offset } = pagination;

  useFiltersOnRouteChange();

  const activeFilterQuery = useQuery(ActiveFiltersQuery);
  const variables = {
    path: location.pathname,
    first: perPage,
    offset,
    orderBy: sortOrder,
    orderByDirection: direction,
    filters: activeFilterQuery.data?.activeFilters || {
      ...defaultActiveFilters
    }
  };

  delete variables.filters.__typename;

  const result = useQuery(routeQuery, {
    variables,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    partialRefetch: true
  });

  const route = result.data?.route;
  const data = result.data?.route?.object;

  return (
    <GenericError
      extraInfo={{
        variables: result.variables,
        data: result.data
      }}
    >
      {data && <Head data={data} route={route} />}
      <RouteResolver
        productPage={productPage}
        categoryPage={categoryPage}
        contentPage={contentPage}
        notFoundPage={notFoundPage}
        LoadingPage={LoadingPage}
        ErrorPage={ErrorPage}
        result={result}
        pathname={location.pathname}
        state={location.state}
        search={location.search}
      />
    </GenericError>
  );
};

export default DynamicRoute;
